import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Spinner } from '@/components/ui'
import { NativeSelect } from '@/components/pos/office/NativeSelect'
import { useSession } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { canAdjustOffline, recordStockCount } from '@/db/repositories/stock'
import { useProducts } from '@/hooks/features/catalog'
import { useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'
import { syncEngine } from '@/sync/SyncEngine'
import { PosModel } from '@/types'

export type StockMove = 'purchase' | 'adjust' | 'transfer'

const TITLE: Record<StockMove, { title: string; description: string; action: string }> = {
  purchase: { title: 'Stock in', description: 'Received goods come into this branch as a new batch.', action: 'Receive stock' },
  adjust: { title: 'Adjust stock', description: 'Enter the counted quantity of each batch.', action: 'Adjust' },
  transfer: { title: 'Transfer out', description: 'Moves stock from a batch here to another branch; it arrives there at once.', action: 'Transfer' },
}

interface Line {
  product: PosModel.Product
  quantity: number
  batch?: string
  purchase_price?: number
  sale_price?: number
  expiry_date?: string
}

/**
 * Stock in, adjustment and transfer out are online, direct actions (POS.md §18.7 Q3, Q8; transfers always): the server
 * moves the stock at once and the device sees it in the next pull. The request names this branch (`branch_id`).
 */
export const StockMoveDialog = ({ move, onClose }: { move: StockMove | null; onClose: () => void }) => {
  const { device } = useSession()
  const online = useOnline()
  const client = useQueryClient()
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [destination, setDestination] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const { data: found } = useProducts({ search: search.trim() || undefined })
  const { data: context } = useQuery({
    queryKey: ['stock-move-context', move],
    queryFn: async () => ({
      batches: await rows('stocks').toArray(),
      offline: await canAdjustOffline(),
      branches: (await rows('branches').toArray()).filter((b) => !b.deleted_at && b._id !== device?.branch_id),
    }),
    enabled: !!move,
  })

  useEffect(() => {
    if (!move) return
    setLines([])
    setSearch('')
    setNote('')
    setDestination('')
  }, [move])

  const batchesOf = useMemo(
    () => (p: PosModel.Product) =>
      (context?.batches ?? [])
        .filter((s) => String(s.product_variation_id) === p.server_id && s.stock_purchase_transaction_id)
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
    [context]
  )

  if (!move) return null
  const t = TITLE[move]
  const needsBatch = move !== 'purchase'
  const localCount = move === 'adjust' && !!context?.offline

  const add = (product: PosModel.Product) => {
    if (!product.server_id) return toast.error(`${product.name} has not synced yet; try again after the next sync.`)
    const batch = batchesOf(product)[0]
    if (needsBatch && !batch) return toast.error(`${product.name} has no stock batch in this branch.`)
    setLines((list) => [...list, { product, quantity: move === 'adjust' ? 0 : 1, batch: batch ? String(batch.stock_purchase_transaction_id) : undefined }])
    setSearch('')
  }
  const patch = (i: number, p: Partial<Line>) => setLines((list) => list.map((l, j) => (j === i ? { ...l, ...p } : l)))

  const submit = async () => {
    if (!device) return
    if (move === 'transfer' && !destination) return toast.error('Choose the branch to send to.')
    const entries = move === 'adjust' ? lines.filter((l) => l.batch && l.quantity >= 0) : lines.filter((l) => l.quantity !== 0)
    if (!entries.length) return toast.error('Add at least one product with a quantity.')
    if (move === 'adjust' && localCount) {
      await recordStockCount(
        entries.map((l) => ({ product_variation_id: l.product.server_id!, stock_purchase_transaction_id: l.batch!, counted: l.quantity })),
        note.trim() || undefined
      )
      toast.success('Stock count saved', { description: 'It syncs with the next connection.' })
      client.invalidateQueries({ queryKey: ['products'] })
      return onClose()
    }
    setBusy(true)
    const base = { branch_id: device.branch_id, description: note.trim() || undefined }
    const res =
      move === 'purchase'
        ? await OfficeModel.purchase({
            ...base,
            entries: entries.map((l) => ({
              product_variation_id: l.product.server_id,
              quantity: l.quantity,
              purchase_price: l.purchase_price,
              sale_price: l.sale_price,
              expiry_date: l.expiry_date || undefined,
            })),
          })
        : move === 'adjust'
          ? await OfficeModel.adjust({
              ...base,
              entries: entries.map((l) => ({ product_variation_id: l.product.server_id, stock_purchase_transaction_id: l.batch, quantity: l.quantity })),
            })
          : await OfficeModel.transfer({
              ...base,
              destination_branch_id: destination,
              entries: entries.map((l) => ({ product_variation_id: l.product.server_id, stock_purchase_transaction_id: l.batch, quantity: l.quantity })),
            })
    setBusy(false)
    if (!res.success) return toast.error(res.error?.message ?? 'The server refused it.')
    toast.success(`${t.title} done`, { description: 'Stock on this device updates with the next sync.' })
    syncEngine.requestSync()
    client.invalidateQueries({ queryKey: ['products'] })
    onClose()
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>
        {!online && !localCount && <p className="text-sm text-destructive">Needs a connection and a signed-in session.</p>}
        {move === 'transfer' && (
          <div className="space-y-1.5">
            <Label htmlFor="dest">To branch</Label>
            <NativeSelect
              id="dest"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              options={[{ value: '', label: 'Choose a branch' }, ...(context?.branches ?? []).map((b) => ({ value: String(b._id), label: String(b.name) }))]}
            />
          </div>
        )}
        <div className="relative">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Add a product: name, SKU or barcode" onCameraScan={setSearch} className="h-11" />
          {search.trim() && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-card shadow-z8">
              {(found?.items ?? []).slice(0, 8).map((p) => (
                <li key={p._id}>
                  <button type="button" className="flex w-full justify-between px-3 py-2 text-left text-sm hover:bg-subdued" onClick={() => add(p)}>
                    <span className="text-ink">{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.stock} in stock</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <ul className="divide-y divide-border-subtle rounded-md border border-border">
          {lines.length === 0 && <li className="p-4 text-center text-sm text-muted-foreground">No products yet.</li>}
          {lines.map((l, i) => (
            <li key={`${l.product._id}-${i}`} className="flex flex-wrap items-end gap-2 px-3 py-2 text-sm">
              <span className="min-w-40 flex-1 self-center font-medium text-ink">{l.product.name}</span>
              {needsBatch && (
                <NativeSelect
                  className="w-48"
                  aria-label="Batch"
                  value={l.batch}
                  onChange={(e) => patch(i, { batch: e.target.value })}
                  options={batchesOf(l.product).map((b) => ({
                    value: String(b.stock_purchase_transaction_id),
                    label: `${b.batch_no ?? 'Batch'} · ${Number(b.quantity)}${b.expiry_date ? ` · exp ${String(b.expiry_date).slice(0, 10)}` : ''}`,
                  }))}
                />
              )}
              <Input
                aria-label={move === 'adjust' ? 'Counted quantity' : 'Quantity'}
                placeholder={move === 'adjust' ? 'Counted' : undefined}
                inputMode="numeric"
                value={String(l.quantity)}
                onChange={(e) => patch(i, { quantity: Math.max(move === 'transfer' || move === 'adjust' ? 0 : -Infinity, parseInt(e.target.value.replace(/[^\d-]/g, '') || '0', 10) || 0) })}
                className="h-11 w-24 text-right tabular-nums"
              />
              {move === 'purchase' && (
                <>
                  <Input aria-label="Cost each" placeholder="Cost each" inputMode="decimal" onChange={(e) => patch(i, { purchase_price: parseFloat(e.target.value) || undefined })} className="h-11 w-28 text-right" />
                  <Input aria-label="Sale price" placeholder="Sale price" inputMode="decimal" onChange={(e) => patch(i, { sale_price: parseFloat(e.target.value) || undefined })} className="h-11 w-28 text-right" />
                  <Input aria-label="Expiry" type="date" onChange={(e) => patch(i, { expiry_date: e.target.value })} className="h-11 w-40" />
                </>
              )}
              <PosButton variant="ghost" size="icon-touch" aria-label="Remove" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                <Trash2 />
              </PosButton>
            </li>
          ))}
        </ul>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional): supplier invoice, count reason…" className="h-11" />
        <DialogFooter>
          <PosButton variant="secondary" onClick={onClose}>
            Cancel
          </PosButton>
          <PosButton onClick={submit} disabled={busy || (!online && !localCount)}>
            {busy && <Spinner className="size-4" />} {t.action}
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
